import { useEffect, useState } from 'react';
import Cookies from 'universal-cookie';
import { AUTO_HIDE_COUNT, AUTO_HIDE_KEY, SESSION_HOUR_DURATION } from 'common/constants';

interface UseStretchBannerAutohide {
  bannerDuration: number;
  autohide: boolean;
  autohideViews: number;
}

interface UseStretchBannerAutohideReturn {
  isAutohide: boolean;
}

export function useStretchBannerAutohide(props: UseStretchBannerAutohide): UseStretchBannerAutohideReturn {
  const { bannerDuration, autohide, autohideViews } = props;

  const [isAutohide, setIsAutohide] = useState<boolean>(false);
  const [cookies, setCookies] = useState<Cookies | null>(null);

  useEffect(() => {
    if (!autohide) return;
    setCookies(new Cookies());
  }, [autohide]);

  // Основная логика скрытия
  useEffect(() => {
    if (!cookies) return;

    const getExpirationDate = () => {
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + bannerDuration);
      return expirationDate;
    };

    const updateAutoHideKey = () => {
      const now = new Date().toISOString();
      cookies.set(AUTO_HIDE_KEY, now, {
        path: '/',
        expires: new Date(Date.now() + bannerDuration * SESSION_HOUR_DURATION),
        sameSite: 'lax',
      });
    };

    const setCounter = (count: number) => {
      cookies.set(AUTO_HIDE_COUNT, count, {
        path: '/',
        expires: getExpirationDate(),
        sameSite: 'lax',
      });
    };

    const shouldResetCounter = (currentCount: string | undefined) => {
      if (!currentCount) return false;

      if (Number.parseInt(currentCount, 10) > autohideViews) {
        const autoHideKeyValue = cookies.get(AUTO_HIDE_KEY);
        if (autoHideKeyValue) {
          const autoHideTime = new Date(autoHideKeyValue).getTime();
          const currentTime = new Date().getTime();
          return currentTime > autoHideTime + bannerDuration * SESSION_HOUR_DURATION;
        }
      }
      return false;
    };

    const initializeNewSession = () => {
      updateAutoHideKey();
      setCounter(1);
      setIsAutohide(false);
    };

    const handleExistingSession = (currentCount: string | undefined) => {
      if (!currentCount) {
        // Если счетчика нет, но есть кука AUTO_HIDE_KEY
        updateAutoHideKey();
        setCounter(1);
        return;
      }

      const newCount = Math.min(Number.parseInt(currentCount, 10) + 1, autohideViews);
      updateAutoHideKey();
      setCounter(newCount);

      if (newCount > autohideViews) {
        setIsAutohide(true);
      }
    };

    // Основная логика выполнения
    const startSessionCookies = cookies.get(AUTO_HIDE_KEY);
    const currentCount = cookies.get(AUTO_HIDE_COUNT);

    if (!startSessionCookies) {
      // Новая сессия
      initializeNewSession();
    } else if (shouldResetCounter(currentCount)) {
      // Сброс счетчика по истечении времени
      initializeNewSession();
    } else {
      // Продолжение существующей сессии
      handleExistingSession(currentCount);
    }
  }, [autohideViews, bannerDuration, cookies]);

  return { isAutohide };
}
